/**
 * Keyboard shortcuts — one keydown listener, a registry of combos. Window-scoped
 * entries only fire for the focused window; `label` doubles as the data source
 * for menus (and a future ⌘K palette).
 *
 * Combo grammar: "mod+w", "mod+shift+p", "j" — `mod` is ⌘ on macOS, Ctrl
 * elsewhere. Single-letter combos never fire while typing in an input.
 */
import { useEffect } from "react";
import { getState } from "./store";

export interface Shortcut {
  combo: string;
  scope: "global" | "window";
  /** window id, required for scope:"window" */
  windowId?: string;
  label: string;
  run: () => void;
}

const registry = new Set<Shortcut>();
let installed = false;

function comboOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("mod");
  if (e.shiftKey) parts.push("shift");
  if (e.altKey) parts.push("alt");
  parts.push(e.key.toLowerCase());
  return parts.join("+");
}

function isTyping(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
}

function onKeyDown(e: KeyboardEvent) {
  const combo = comboOf(e);
  // bare letters are dangerous while typing; modified combos are fine
  if (!combo.includes("mod+") && isTyping(e)) return;
  const focused = getState().stack.at(-1);
  for (const s of registry) {
    if (s.combo !== combo) continue;
    if (s.scope === "window" && s.windowId !== focused) continue;
    e.preventDefault();
    s.run();
    return;
  }
}

export function registerShortcut(s: Shortcut): () => void {
  if (!installed) {
    window.addEventListener("keydown", onKeyDown);
    installed = true;
  }
  registry.add(s);
  return () => registry.delete(s);
}

/** React helper — register for the lifetime of the calling component. */
export function useShortcut(s: Shortcut, deps: unknown[] = []): void {
  // deps come from the caller; s is deliberately not one of them.
  useEffect(() => registerShortcut(s), deps);
}

export function listShortcuts(): Shortcut[] {
  return [...registry];
}
